


import React from 'react';

function InfoDetails({ title, onClose }) {

  const data = {
    'Haemoglobin': {
      heading: 'Haemoglobin',
      description: 'Protein in red blood cells that carries oxygen.',
      ranges: [
        { label: 'Male (Healthy)', color: '#4caf50', value: '13.5 – 17.5 g/dL' },
        { label: 'Female (Healthy)', color: '#4caf50', value: '12.0 – 15.5 g/dL' },
      ],
      additionalInfo: [
        { label: 'Low (Male)', color: '#dc3545', description: '< 13.5 g/dL — may indicate anaemia' },
        { label: 'Normal (Male)', color: '#4caf50', description: '13.5 – 17.5 g/dL — healthy' },
        { label: 'High (Male)', color: '#ff9800', description: '> 17.5 g/dL — dehydration or other conditions' },
        { label: 'Low (Female)', color: '#dc3545', description: '< 12.0 g/dL — may indicate anaemia' },
        { label: 'Normal (Female)', color: '#4caf50', description: '12.0 – 15.5 g/dL — healthy' },
        { label: 'High (Female)', color: '#ff9800', description: '> 15.5 g/dL — dehydration or other conditions' },
      ],
    },
    'Heart Rate': {
      heading: 'Heart Rate',
      description: 'Number of times your heart beats per minute.',
      ranges: [
        { label: 'Low', color: '#f0ad4e', value: '< 60 bpm' },
        { label: 'Healthy', color: '#4caf50', value: '60 – 100 bpm' },
        { label: 'Elevated', color: '#dc3545', value: '> 100 bpm' },
      ],
      additionalInfo: [
        { label: 'Bradycardia', color: '#f0ad4e', description: '< 60 bpm — slow heart rate' },
        { label: 'Normal', color: '#4caf50', description: '60 – 100 bpm' },
        { label: 'Tachycardia', color: '#dc3545', description: '> 100 bpm — fast heart rate' },
      ],
    },
    'Oxygen Level': {
      heading: 'Oxygen Level',
      description: 'Amount of oxygen in your blood (SpO2). Normal is 95–100%.',
      ranges: [
        { label: 'Low', color: '#dc3545', value: '< 90%' },
        { label: 'Concerning', color: '#f0ad4e', value: '90 – 95%' },
        { label: 'Healthy', color: '#4caf50', value: '95 – 100%' },
      ],
      additionalInfo: [
        { label: 'Hypoxemia', color: '#dc3545', description: '< 90% — requires immediate attention' },
        { label: 'Concerning', color: '#f0ad4e', description: '90 – 95% — monitor closely' },
        { label: 'Healthy', color: '#4caf50', description: '95 – 100% — optimal' },
      ],
    },
    'Breathing Rate': {
      heading: 'Breathing Rate',
      description: 'Number of breaths per minute.',
      ranges: [
        { label: 'Low', color: '#f0ad4e', value: '< 12 bpm' },
        { label: 'Healthy', color: '#4caf50', value: '12 – 20 bpm' },
        { label: 'Elevated', color: '#dc3545', value: '> 20 bpm' },
      ],
      additionalInfo: [
        { label: 'Bradypnea', color: '#f0ad4e', description: '< 12 breaths/min — slow' },
        { label: 'Normal', color: '#4caf50', description: '12 – 20 breaths/min' },
        { label: 'Tachypnea', color: '#dc3545', description: '> 20 breaths/min — fast' },
      ],
    },
    'Systolic Blood Pressure': {
      heading: 'Systolic BP',
      description: 'Top number — pressure in arteries when your heart beats.',
      ranges: [
        { label: 'Low', color: '#f0ad4e', value: '< 90 mm Hg' },
        { label: 'Healthy', color: '#4caf50', value: '90 – 120 mm Hg' },
        { label: 'Elevated', color: '#ff9800', value: '120 – 129 mm Hg' },
        { label: 'Stage 1', color: '#dc3545', value: '130 – 139 mm Hg' },
        { label: 'Stage 2', color: '#b22222', value: '≥ 140 mm Hg' },
      ],
      additionalInfo: [],
    },
    'Diastolic Blood Pressure': {
      heading: 'Diastolic BP',
      description: 'Bottom number — pressure in arteries when heart is resting.',
      ranges: [
        { label: 'Low', color: '#f0ad4e', value: '< 60 mm Hg' },
        { label: 'Healthy', color: '#4caf50', value: '60 – 79 mm Hg' },
        // No "Elevated" band for diastolic: that category is systolic-only (120–129).
        { label: 'Stage 1', color: '#dc3545', value: '80 – 89 mm Hg' },
        { label: 'Stage 2', color: '#b22222', value: '≥ 90 mm Hg' },
      ],
      additionalInfo: [],
    },
    'Stress Index': {
      heading: 'Stress Index',
      description: 'Measures body stress based on HRV and autonomic nervous system activity.',
      ranges: [
        { label: 'Low Stress', color: '#4caf50', value: '0 – 25%' },
        { label: 'Stressed', color: '#f0ad4e', value: '25 – 50%' },
        { label: 'High Stress', color: '#dc3545', value: '50 – 75%' },
        { label: 'Extreme Stress', color: '#b22222', value: '75 – 100%' },
      ],
      additionalInfo: [
        { label: 'Healthy', color: '#4caf50', description: 'Low stress, body balanced' },
        { label: 'Stressed', color: '#f0ad4e', description: 'Moderate stress, recovery needed' },
        { label: 'High / Extreme', color: '#dc3545', description: 'High risk for fatigue and burnout' },
      ],
    },
    'Heart Rate Variability': {
      heading: 'Heart Rate Variability',
      description: 'Variation between heartbeats — indicates heart health and stress levels.',
      ranges: [
        { label: 'Low', color: '#f0ad4e', value: '< 50 ms' },
        { label: 'Healthy', color: '#4caf50', value: '50 – 100 ms' },
        // Higher HRV is generally a good sign — label/colour must match "Resilient" below.
        { label: 'High (Resilient)', color: '#4caf50', value: '> 100 ms' },
      ],
      additionalInfo: [
        { label: 'Stressed', color: '#f0ad4e', description: '< 50 ms — reduced adaptability' },
        { label: 'Stable', color: '#4caf50', description: '50 – 100 ms — balanced' },
        { label: 'Resilient', color: '#4caf50', description: '> 100 ms — excellent adaptability; very high values may need context' },
      ],
    },
    'BMI': {
      heading: 'BMI',
      description: 'Body fat measure based on height and weight.',
      ranges: [
        { label: 'Underweight', color: '#f0ad4e', value: '< 18.5 kg/m²' },
        { label: 'Healthy', color: '#4caf50', value: '18.5 – 24.9 kg/m²' },
        { label: 'Overweight', color: '#ff9800', value: '25 – 29.9 kg/m²' },
        { label: 'Obese', color: '#dc3545', value: '30 – 40 kg/m²' },
        { label: 'Severe Obesity', color: '#b22222', value: '> 40 kg/m²' },
      ],
      additionalInfo: [
        { label: 'Underweight', color: '#f0ad4e', description: 'May indicate malnutrition' },
        { label: 'Healthy', color: '#4caf50', description: 'Lower risk of weight-related issues' },
        { label: 'Overweight', color: '#ff9800', description: 'Increased cardiovascular risk' },
        { label: 'Obese', color: '#dc3545', description: 'High risk for heart disease, diabetes' },
        { label: 'Severe Obesity', color: '#b22222', description: 'Medical intervention may be needed' },
      ],
    },
    'Burnout Risk': {
      heading: 'Burnout Risk',
      description: 'Likelihood of exhaustion from prolonged stress and overwork.',
      ranges: [
        { label: 'Low', color: '#4caf50', value: '0 – 30%' },
        { label: 'Moderate', color: '#f0ad4e', value: '30 – 60%' },
        { label: 'High', color: '#ff9800', value: '60 – 80%' },
        { label: 'Severe', color: '#dc3545', value: '80 – 100%' },
      ],
      additionalInfo: [
        { label: 'Low', color: '#4caf50', description: 'Healthy recovery and low stress' },
        { label: 'Moderate', color: '#f0ad4e', description: 'Stress may be affecting well-being' },
        { label: 'High', color: '#ff9800', description: 'Elevated stress and fatigue symptoms' },
        { label: 'Severe', color: '#dc3545', description: 'Critical burnout signs' },
      ],
    },
    'Recovery Score': {
      heading: 'Recovery Score',
      description: "Body's ability to rest and regain energy after stress or exercise.",
      ranges: [
        { label: 'Severely Impaired', color: '#dc3545', value: '0 – 20%' },
        { label: 'Poor', color: '#ff9800', value: '20 – 40%' },
        { label: 'Moderate', color: '#f0ad4e', value: '40 – 70%' },
        { label: 'Excellent', color: '#4caf50', value: '70 – 100%' },
      ],
      additionalInfo: [
        { label: 'Excellent', color: '#4caf50', description: 'Optimal energy and recovery (70–100%)' },
        { label: 'Moderate', color: '#f0ad4e', description: 'Some fatigue but manageable (40–70%)' },
        { label: 'Poor', color: '#ff9800', description: 'Significant fatigue (20–40%)' },
        { label: 'Severely Impaired', color: '#dc3545', description: 'Intervention required (0–20%)' },
      ],
    },
    'Anxiety Risk': {
      heading: 'Anxiety Risk',
      description: 'Likelihood of anxiety symptoms based on HRV and stress indicators.',
      ranges: [
        { label: 'Low', color: '#4caf50', value: '0 – 30%' },
        { label: 'Moderate', color: '#f0ad4e', value: '30 – 60%' },
        { label: 'High', color: '#ff9800', value: '60 – 80%' },
        { label: 'Severe', color: '#dc3545', value: '80 – 100%' },
      ],
      additionalInfo: [
        { label: 'Low', color: '#4caf50', description: 'Normal indicators (0–30%)' },
        { label: 'Moderate', color: '#f0ad4e', description: 'Some anxiety symptoms (30–60%)' },
        { label: 'High', color: '#ff9800', description: 'Strong anxiety signs (60–80%)' },
        { label: 'Severe', color: '#dc3545', description: 'Requires treatment (80–100%)' },
      ],
    },
    'Fatigue Risk': {
      heading: 'Fatigue Risk',
      description: 'Likelihood of reduced performance due to tiredness.',
      ranges: [
        { label: 'Low', color: '#4caf50', value: '0 – 30%' },
        { label: 'Moderate', color: '#f0ad4e', value: '30 – 60%' },
        { label: 'High', color: '#ff9800', value: '60 – 80%' },
        { label: 'Severe', color: '#dc3545', value: '80 – 100%' },
      ],
      additionalInfo: [
        { label: 'Low', color: '#4caf50', description: 'Good recovery (0–30%)' },
        { label: 'Moderate', color: '#f0ad4e', description: 'Manageable fatigue (30–60%)' },
        { label: 'High', color: '#ff9800', description: 'Strong fatigue symptoms (60–80%)' },
        { label: 'Severe', color: '#dc3545', description: 'Intervention required (80–100%)' },
      ],
    },
  };

  const content = data[title];

  if (!content) {
    return <p className="text-gray-500 text-center py-4">No information available.</p>;
  }

  return (
    <div style={{ width: '100%' }}>
      {/* Heading */}
      <h4 style={{
        fontSize: '16px',
        fontWeight: '700',
        color: '#1E3A8A',
        marginBottom: '6px',
        paddingBottom: '8px',
        borderBottom: '1px solid #e5e7eb',
      }}>
        {content.heading}
      </h4>

      {/* Description */}
      {content.description && (
        <p style={{
          fontSize: '13px',
          color: '#4b5563',
          marginBottom: '12px',
          lineHeight: '1.5',
        }}>
          {content.description}
        </p>
      )}

      {/* Range pills */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '12px' }}>
        {content.ranges?.map((range, index) => (
          <div key={index} style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '6px 10px',
            borderRadius: '8px',
            backgroundColor: `${range.color}15`,
            border: `1px solid ${range.color}40`,
          }}>
            <span style={{ fontSize: '12px', fontWeight: '600', color: range.color }}>
              {range.label}
            </span>
            <span style={{ fontSize: '12px', color: '#374151', fontWeight: '500' }}>
              {range.value}
            </span>
          </div>
        ))}
      </div>

      {/* Additional info */}
      {content.additionalInfo?.length > 0 && (
        <div style={{
          backgroundColor: '#f8fafc',
          borderRadius: '8px',
          padding: '10px',
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
        }}>
          {content.additionalInfo.map((item, index) => (
            <div key={index} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              <span style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: item.color || '#9ca3af',
                marginTop: '5px',
                flexShrink: 0,
              }} />
              <p style={{ fontSize: '12px', color: '#4b5563', margin: 0, lineHeight: '1.5' }}>
                <strong style={{ color: '#1f2937' }}>{item.label}:</strong> {item.description}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default InfoDetails;